import React, { useState } from 'react';
import ConfirmModal from '../../../components/ConfirmModal';
import { CampaignItemIcon } from '../../../../../Sidebar/icons';
import { DependenciesSection, DeliverablesSection } from '../../../../shared/ConditionsSections';

/**
 * MetadataSection Component
 * 
 * Displays SECONDARY task metadata in the sidebar.
 * Primary metadata (Status, Priority, Difficulty, Tags, Dates) has been
 * moved to the MetadataBar component in the content section.
 * 
 * This section now shows:
 * 1. Mission dependencies / unlock conditions (via DependenciesSection)
 * 2. Deliverables conditions (via DeliverablesSection)
 * 3. Nested campaign (styled like mission card)
 * 
 * NOTE: Custom fields are handled separately by CustomFieldsSidebarSection.
 */

const MetadataSection = ({ 
  task,
  allTasks = [],
  statuses = [],
  nestedSprints = [],
  hasNestedSprint = false,
  nestedSprint = null,
  // Nested sprint callbacks
  onOpenNestedSprint,
  onRemoveNestedSprint,
  // Conditions editing
  onConditionsChange,
  // Deliverables editing
  onEditDeliverables,
  // Deliverables submission
  onSubmitDeliverable,
  // Clear deliverable submission
  onClearDeliverable,
}) => {
  // State for delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Progress for nested sprint
  const nestedProgress = nestedSprint?.progress || 0;

  return (
    <section className="mfv-metadata">
      <div className="mfv-metadata__list">
        {/* Mission Dependencies (Unlock Conditions) */}
        <DependenciesSection
          task={task}
          allTasks={allTasks}
          statuses={statuses}
          nestedSprints={nestedSprints}
          onConditionsChange={onConditionsChange}
        />

        {/* Deliverables Conditions (Required Outputs) */}
        <DeliverablesSection
          task={task}
          onEditDeliverables={onEditDeliverables}
          onSubmitDeliverable={onSubmitDeliverable}
          onClearDeliverable={onClearDeliverable}
        />

        {/* Nested Campaign - styled like mission card */}
        {hasNestedSprint && nestedSprint && (
          <div className="mfv-metadata__nested-group">
            <span className="mfv-metadata__nested-header">Nested campaign</span>
            <div className="mfv-metadata__nested-btn">
              {/* Icon */}
              <span 
                className="mfv-metadata__nested-icon"
                onClick={() => onOpenNestedSprint?.(nestedSprint)}
              >
                <CampaignItemIcon size={14} />
              </span>
              
              {/* Middle: name + progress (clickable) */}
              <div 
                className="mfv-metadata__nested-middle"
                onClick={() => onOpenNestedSprint?.(nestedSprint)}
                title={`Open nested campaign: ${nestedSprint.name}`}
              >
                <span className="mfv-metadata__nested-name">{nestedSprint.name}</span>
                <div className="mfv-metadata__nested-progress">
                  <div 
                    className={`mfv-metadata__nested-progress-fill ${nestedProgress === 100 ? 'mfv-metadata__nested-progress-fill--complete' : ''}`}
                    style={{ width: `${nestedProgress}%` }}
                  />
                </div>
              </div>
              
              {/* Delete button */}
              <span
                className="mfv-metadata__nested-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                title="Move to trash"
                role="button"
                tabIndex={0}
              >
                ×
              </span>
            </div>
            
            {/* Delete confirmation modal */}
            <ConfirmModal
              isOpen={showDeleteConfirm}
              title="Move to Trash"
              message={`Move "${nestedSprint.name}" to trash? The mission itself stays — it just won't contain a nested campaign anymore. You can restore the campaign later from the Trash.`}
              confirmLabel="Move to Trash"
              cancelLabel="Cancel"
              confirmVariant="warning"
              icon="🗑"
              onConfirm={() => {
                setShowDeleteConfirm(false);
                onRemoveNestedSprint?.();
              }}
              onCancel={() => setShowDeleteConfirm(false)}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default MetadataSection;
